import { z } from 'zod';

const shortText = (max: number) => z.string().trim().max(max).optional();

/**
 * Rechtliche Einheit / Gesellschaft (ADR 0029). Mehrere je Tenant; teilen sich
 * Stammdaten, unterscheiden sich in offiziellen Belegen (Briefpapier, Steuersatz,
 * Nummernkreis-Ziffer).
 */
export const createLegalEntitySchema = z.object({
  code: z.string().trim().min(1).max(10),
  name: z.string().trim().min(1).max(200),
  /** Ziffer im Belegnummern-Format (z. B. 1=AT, 2=DE). Je Tenant eindeutig. */
  numberDigit: z.number().int().min(1).max(9),
  /** ISO-Land der Gesellschaft (z. B. "AT", "DE"). */
  countryCode: z.string().trim().min(2).max(3),
  /** Inlandssatz in % (AT 20 / DE 19). */
  defaultTaxRatePercent: z.number().min(0).max(100).default(20),
  uid: shortText(50),
  street: shortText(200),
  postalCode: shortText(20),
  city: shortText(120),
  country: shortText(120),
  email: z.string().trim().email().max(200).optional().or(z.literal('')),
  phone: shortText(50),
  website: shortText(200),
  iban: shortText(50),
  bic: shortText(20),
  bankName: shortText(120),
  isDefault: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(99).optional(),
  isActive: z.boolean().optional(),
});
export type CreateLegalEntityInput = z.infer<typeof createLegalEntitySchema>;

export const updateLegalEntitySchema = createLegalEntitySchema.partial();
export type UpdateLegalEntityInput = z.infer<typeof updateLegalEntitySchema>;

export const legalEntityResponseSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  numberDigit: z.number().int(),
  countryCode: z.string(),
  defaultTaxRatePercent: z.number(),
  uid: z.string().nullable(),
  street: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  iban: z.string().nullable(),
  bic: z.string().nullable(),
  bankName: z.string().nullable(),
  /** Logo als Data-URL für die Vorschau (null = keins). */
  logoDataUrl: z.string().nullable(),
  /** Briefpapier als Data-URL für die Vorschau (null = keins). */
  letterheadDataUrl: z.string().nullable(),
  isDefault: z.boolean(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  updatedAt: z.string().datetime().nullable(),
});
export type LegalEntityResponse = z.infer<typeof legalEntityResponseSchema>;
